import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import Page from "../Page";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";
import { alertSlice } from "src/store/alert/alert.slice";
import whatsappService from "src/services/whatsappService";

const fmt = (value) => (value ? new Date(value).toLocaleString() : "-");

// Best-effort field access across the shapes ai-cct may return.
const pick = (obj, keys, fallback = "-") => {
  for (const k of keys) {
    const v = k.split(".").reduce((o, part) => (o ? o[part] : undefined), obj);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
};

const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.conversations)) return data.conversations;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.messages)) return data.messages;
  return [];
};

// The use cases where the system sends a WhatsApp message. WhatsApp is limited
// to these three charger events (cms-websocket WhatsAppNotificationSender only
// sends for them); every other event is email-only, so there is nothing to map
// here for them. The key matches the cms-websocket NotificationFeature name.
const WHATSAPP_USE_CASES = [
  { key: "OcppStationBooted", label: "Charger booted" },
  { key: "OcppStationStatusFailure", label: "Connector faulted" },
  { key: "OcppStationWebSocketDisconnected", label: "Station disconnected" },
];

// Keys WhatsApp can send for — used to prune any stale template mappings (left
// over from events that are now email-only) when the connection is saved.
const WHATSAPP_USE_CASE_KEYS = WHATSAPP_USE_CASES.map((uc) => uc.key);

// ai-cct returns { items: [{ name, language, variable_count, ... }] } (APPROVED).
const normalizeTemplates = (data) => {
  const list = Array.isArray(data)
    ? data
    : data?.items || data?.templates || data?.data || [];
  return list
    .map((t) =>
      typeof t === "string"
        ? { name: t, label: t }
        : {
            name: t.name,
            label:
              t.name +
              (t.language ? ` (${t.language})` : "") +
              (t.variable_count ? ` — ${t.variable_count} var(s)` : ""),
          }
    )
    .filter((t) => t.name);
};

const WhatsAppAdmin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isGrandParent = useIsGrandParent();

  const alert = (message, severity = "success") =>
    dispatch(alertSlice.actions.setAlert({ open: true, message, severity }));

  // Super-admin (GP) only. Self-guard, matching AuditLogs.
  useEffect(() => {
    if (!isGrandParent) {
      alert(
        "Not Authorized - Grand Parent (super admin) access required",
        "error"
      );
      navigate("/dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrandParent]);

  const [tab, setTab] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  // Decide where to land before showing data: if WhatsApp isn't configured yet,
  // open on Connection Settings rather than greeting the super admin with a
  // "not configured" Messages view.
  useEffect(() => {
    if (!isGrandParent) return undefined;
    let active = true;
    (async () => {
      const cfg = await whatsappService.getConfig();
      if (!active) return;
      const on = Boolean(cfg && (Number(cfg.enabled) === 1 || cfg.enabled === true));
      setEnabled(on);
      setTab(on ? 0 : 1);
      setConfigLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [isGrandParent]);

  if (!isGrandParent) return null;

  return (
    <Page title="WhatsApp">
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            WhatsApp
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Super-admin WhatsApp console. View sent &amp; received messages and
            configure the connection to the messaging service.
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Messages" />
          <Tab label="Connection Settings" />
        </Tabs>

        {tab === 0 ? (
          <MessagesTab
            alert={alert}
            enabled={enabled}
            configLoading={configLoading}
            onConfigure={() => setTab(1)}
          />
        ) : (
          <SettingsTab alert={alert} onSaved={(on) => setEnabled(Boolean(on))} />
        )}
      </Container>
    </Page>
  );
};

// ---------------------------------------------------------------------------
// Messages: list conversations (sent + received), drill into one for history.
// ---------------------------------------------------------------------------
const MessagesTab = ({ alert, enabled, configLoading, onConfigure }) => {
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);

  useEffect(() => {
    if (configLoading) return undefined;
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const data = await whatsappService.getConversations({ limit: 100 });
        if (active) setConversations(asArray(data));
      } catch (error) {
        if (active)
          alert(
            error?.response?.data?.message || "Failed to load conversations",
            "error"
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, configLoading]);

  if (configLoading || (enabled && loading)) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!enabled) {
    return (
      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            WhatsApp isn&apos;t set up yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add the messaging service URL and API key and enable the connection
            to start sending and viewing WhatsApp messages.
          </Typography>
          <Button variant="contained" onClick={onConfigure}>
            Open Connection Settings
          </Button>
        </CardContent>
      </Card>
    );
  }

  const openConversation = async (conv) => {
    const id = pick(conv, ["id", "conversation_id", "_id"], null);
    if (!id) return;
    setSelected(conv);
    setMsgLoading(true);
    try {
      const data = await whatsappService.getMessages(id, { limit: 200 });
      setMessages(asArray(data));
    } catch (error) {
      alert(
        error?.response?.data?.message || "Failed to load messages",
        "error"
      );
    } finally {
      setMsgLoading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={selected ? 5 : 12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Conversations ({conversations.length})
            </Typography>
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Contact</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Last activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conversations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          No conversations yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    conversations.map((conv, i) => {
                      const id = pick(conv, ["id", "conversation_id", "_id"], i);
                      const isSel =
                        selected &&
                        pick(selected, ["id", "conversation_id", "_id"], -1) ===
                          id;
                      return (
                        <TableRow
                          key={id}
                          hover
                          selected={isSel}
                          sx={{ cursor: "pointer" }}
                          onClick={() => openConversation(conv)}
                        >
                          <TableCell>
                            {pick(conv, [
                              "customer_name",
                              "customer_phone",
                              "customer_email",
                              "title",
                              "name",
                            ])}
                          </TableCell>
                          <TableCell>
                            {pick(conv, ["channel", "conversation_type"], "whatsapp")}
                          </TableCell>
                          <TableCell>
                            {fmt(
                              pick(
                                conv,
                                ["updatedAt", "updated_at", "started_at"],
                                null
                              )
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {selected && (
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Messages
                </Typography>
                <Button size="small" onClick={() => setSelected(null)}>
                  Close
                </Button>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              {msgLoading ? (
                <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No messages in this conversation.
                </Typography>
              ) : (
                <Stack spacing={1.5} sx={{ maxHeight: 520, overflowY: "auto" }}>
                  {messages.map((m, i) => {
                    const direction = String(
                      pick(
                        m,
                        ["metadata.direction", "direction", "role"],
                        "outbound"
                      )
                    ).toLowerCase();
                    const inbound =
                      direction === "inbound" || direction === "user";
                    return (
                      <Box
                        key={pick(m, ["id", "_id"], i)}
                        sx={{
                          alignSelf: inbound ? "flex-start" : "flex-end",
                          maxWidth: "80%",
                          bgcolor: inbound ? "grey.100" : "primary.light",
                          color: inbound ? "text.primary" : "primary.contrastText",
                          borderRadius: 2,
                          px: 1.5,
                          py: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {pick(m, ["content", "body", "text", "message"], "")}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mt: 0.5, opacity: 0.8 }}
                        >
                          <Chip
                            size="small"
                            label={inbound ? "Received" : "Sent"}
                            color={inbound ? "default" : "primary"}
                            variant="outlined"
                          />
                          <Typography variant="caption">
                            {fmt(
                              pick(m, ["createdAt", "created_at", "timestamp"], null)
                            )}
                          </Typography>
                          {pick(m, ["status", "metadata.status"], null) !==
                            null && (
                            <Typography variant="caption">
                              · {pick(m, ["status", "metadata.status"], "")}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

// ---------------------------------------------------------------------------
// Connection settings: ai-cct base URL + API key + enable + default channel,
// plus a test send.
// ---------------------------------------------------------------------------
const SettingsTab = ({ alert, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    aicct_base_url: "",
    aicct_api_key: "", // blank => keep existing
    default_channel: "whatsapp",
    default_template: "",
    template_map: {}, // use case -> template name
  });
  const [hasKey, setHasKey] = useState(false);
  const [maskedKey, setMaskedKey] = useState("");
  const [templates, setTemplates] = useState([]);

  const [test, setTest] = useState({
    to: "",
    body: "",
    templateName: "",
    params: ["", ""],
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const config = await whatsappService.getConfig();
      if (active) {
        if (config) {
          setForm((f) => ({
            ...f,
            enabled: Number(config.enabled) === 1 || config.enabled === true,
            aicct_base_url: config.aicct_base_url || "",
            default_channel: config.default_channel || "whatsapp",
            default_template: config.default_template || "",
            template_map:
              config.template_map && typeof config.template_map === "object"
                ? config.template_map
                : {},
            aicct_api_key: "",
          }));
          setHasKey(Boolean(config.has_api_key));
          setMaskedKey(config.has_api_key ? config.aicct_api_key || "" : "");
        }
        setLoading(false);
      }
      // Templates need an enabled connection server-side; failures resolve to [].
      const tpls = await whatsappService.getTemplates();
      if (active) setTemplates(normalizeTemplates(tpls));
    })();
    return () => {
      active = false;
    };
  }, []);

  const reloadTemplates = async () => {
    const tpls = await whatsappService.getTemplates();
    setTemplates(normalizeTemplates(tpls));
  };

  const onChange = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const save = async () => {
    setSaving(true);
    try {
      // Keep only the mappings for events WhatsApp actually sends for; drop any
      // stale entries for events that are now email-only.
      const prunedTemplateMap = Object.fromEntries(
        Object.entries(form.template_map || {}).filter(([k]) =>
          WHATSAPP_USE_CASE_KEYS.includes(k)
        )
      );
      const payload = {
        enabled: form.enabled,
        aicct_base_url: form.aicct_base_url.trim(),
        default_channel: form.default_channel,
        default_template: form.default_template || null,
        template_map: prunedTemplateMap,
      };
      // Only send the key when the admin typed a new one.
      if (form.aicct_api_key.trim()) payload.aicct_api_key = form.aicct_api_key.trim();
      const saved = await whatsappService.updateConfig(payload);
      if (saved) {
        setHasKey(Boolean(saved.has_api_key));
        setMaskedKey(saved.has_api_key ? saved.aicct_api_key || "" : "");
        setForm((f) => ({ ...f, aicct_api_key: "" }));
      }
      // Keep the parent's Messages-tab gating in sync with the new enabled state.
      if (onSaved) {
        onSaved(
          saved
            ? Number(saved.enabled) === 1 || saved.enabled === true
            : form.enabled
        );
      }
      alert("WhatsApp connection saved");
      // Enabling/attaching a key may now let us list templates.
      reloadTemplates();
    } catch (error) {
      alert(
        error?.response?.data?.message || "Failed to save WhatsApp connection",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!test.to.trim()) {
      alert("Enter a phone number to test", "error");
      return;
    }
    if (!test.templateName && !test.body.trim()) {
      alert("Pick a template or enter a message to test", "error");
      return;
    }
    setSending(true);
    try {
      if (test.templateName) {
        await whatsappService.sendMessage({
          to: test.to.trim(),
          templateName: test.templateName,
          params: test.params.map((p) => (p || "").trim()),
        });
      } else {
        await whatsappService.sendMessage({
          to: test.to.trim(),
          body: test.body.trim(),
        });
      }
      alert("Test message sent");
      setTest({ to: "", body: "", templateName: "", params: ["", ""] });
    } catch (error) {
      alert(
        error?.response?.data?.message || "Failed to send test message",
        "error"
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Connection
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            The messaging service (ai-cct) base URL and company API key. The same
            connection is used to send WhatsApp notifications.
          </Typography>

          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch checked={form.enabled} onChange={onChange("enabled")} />
              }
              label={form.enabled ? "Enabled" : "Disabled"}
            />
            <TextField
              label="ai-cct Base URL"
              placeholder="https://ai-cct.example.com"
              value={form.aicct_base_url}
              onChange={onChange("aicct_base_url")}
              fullWidth
            />
            <TextField
              label="API Key"
              type="password"
              value={form.aicct_api_key}
              onChange={onChange("aicct_api_key")}
              placeholder={
                hasKey
                  ? `Configured (${maskedKey || "••••"}) — leave blank to keep`
                  : "Paste the ai-cct company API key (rk_…)"
              }
              helperText={
                hasKey
                  ? "A key is stored. Enter a new value only to replace it."
                  : "Required to send and read WhatsApp messages."
              }
              fullWidth
            />
            <TextField
              label="Default Channel"
              select
              value={form.default_channel}
              onChange={onChange("default_channel")}
              sx={{ maxWidth: 240 }}
            >
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
            </TextField>
            <TextField
              label="Default notification template"
              select
              value={form.default_template || ""}
              onChange={onChange("default_template")}
              helperText="Fallback template used for any use case below that has no template mapped. Templates take 2 params: charger id, then address."
              fullWidth
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.name} value={t.name}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>

            <Divider sx={{ my: 1 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Template per use case
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1.5 }}
              >
                WhatsApp is sent only for these three charger events — Charger
                booted, Connector faulted and Station disconnected; all other
                events are email-only. Map an approved template to each event; the
                system sends that template when the event fires. Templates take 2
                params: charger id, then address.
              </Typography>
              <Stack spacing={2}>
                {WHATSAPP_USE_CASES.map((uc) => (
                  <TextField
                    key={uc.key}
                    label={uc.label}
                    select
                    value={form.template_map?.[uc.key] || ""}
                    onChange={(e) =>
                      setForm((f) => {
                        const next = { ...(f.template_map || {}) };
                        if (e.target.value) next[uc.key] = e.target.value;
                        else delete next[uc.key];
                        return { ...f, template_map: next };
                      })
                    }
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>None (use default / skip)</em>
                    </MenuItem>
                    {templates.map((t) => (
                      <MenuItem key={t.name} value={t.name}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ))}
              </Stack>
            </Box>

            <Box>
              <Button variant="contained" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Send a test message
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Verify the connection by sending a WhatsApp message.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="To (phone, E.164)"
              placeholder="+14155551234"
              value={test.to}
              onChange={(e) => setTest((t) => ({ ...t, to: e.target.value }))}
              sx={{ maxWidth: 320 }}
            />
            <TextField
              label="Template (optional)"
              select
              value={test.templateName}
              onChange={(e) =>
                setTest((t) => ({ ...t, templateName: e.target.value }))
              }
              helperText="Pick an approved template, or leave as None to send free text."
              sx={{ maxWidth: 360 }}
            >
              <MenuItem value="">
                <em>None (free text)</em>
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.name} value={t.name}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            {test.templateName ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ maxWidth: 520 }}
              >
                <TextField
                  label="{{1}} Charger id"
                  value={test.params[0]}
                  onChange={(e) =>
                    setTest((t) => ({
                      ...t,
                      params: [e.target.value, t.params[1]],
                    }))
                  }
                  fullWidth
                />
                <TextField
                  label="{{2}} Address"
                  value={test.params[1]}
                  onChange={(e) =>
                    setTest((t) => ({
                      ...t,
                      params: [t.params[0], e.target.value],
                    }))
                  }
                  fullWidth
                />
              </Stack>
            ) : (
              <TextField
                label="Message"
                value={test.body}
                onChange={(e) =>
                  setTest((t) => ({ ...t, body: e.target.value }))
                }
                multiline
                minRows={2}
                fullWidth
              />
            )}
            <Box>
              <Button
                variant="outlined"
                onClick={sendTest}
                disabled={sending || !form.enabled}
              >
                {sending ? "Sending…" : "Send test"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default WhatsAppAdmin;
